import React from 'react';
import {
  Alert,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DollarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { TransportRouteDetailDto } from '../dto/transport';
import TransportFormSection from './TransportFormSection';
import { formatMoneyPl } from '../utils/currency';
import {
  DEFAULT_TRANSPORT_CURRENCY,
  ldmBasisMetricLabelKey,
  type LdmFallbackMetricKey,
  type SharedTransportPreviewRow,
} from './sharedTransportFormUtils';
import {
  buildCostAllocationMethodOptionDefs,
  MAX_MONEY_AMOUNT,
  POLISH_DECIMAL_INPUT_NUMBER_PROPS,
  type TransportCostAllocationWarning,
} from '../utils/transportCostAllocation';

const { Text } = Typography;

interface TransportCostsSectionProps {
  transport?: TransportRouteDetailDto | null;
  canEditTransportCosts: boolean;
  showRateFields: boolean;
  multiOrder: boolean;
  hasMixedQuantityUnits: boolean;
  // Drop MANUAL from the item-level "Podział kosztu na pozycje" picker (split
  // mode can't carry a manual per-line override). Single boolean instead of the
  // old rebuilt option list, whose labels were never rendered — the picker
  // contents come from buildCostAllocationMethodOptionDefs below.
  excludeManual: boolean;
  allocationPreview: SharedTransportPreviewRow[];
  previewColumns: ColumnsType<SharedTransportPreviewRow>;
  currency?: string;
  // Count of loaded cargo lines (quantity > 0). The item-level "Podział kosztu
  // na pozycje" split is hidden when there is nothing to split (≤ 1 line).
  cargoLineCount: number;
  warnings: TransportCostAllocationWarning[];
  renderAllocationWarning: (warning: TransportCostAllocationWarning, index: number) => React.ReactNode;
  // INDIA (HIGH "silent LDM fallback must be visible"): set (non-undefined)
  // ONLY when the operator picked LDM as the between-orders key AND the
  // resolved basis is NOT real LDM for every order — `null` = no common
  // fallback metric at all (equal/zero-basis split); one of the 4 metric keys
  // = that metric was used uniformly instead. Drives the warning Alert below.
  // Absent (undefined) when every order has real LDM or the method isn't LDM.
  ldmFallbackMetric?: LdmFallbackMetricKey | null;
  // F5 (soft mirroring): called the moment the operator changes the
  // item-level split select THEMSELVES, so the parent stops suggest-syncing
  // it from the between-orders key.
  onCostAllocationMethodTouched: () => void;
  // The between-orders key has a legacy LDM default, but LDM is only a valid
  // item-level split when the cargo has package/manual LDM data.  Keep the
  // canonical create default (commercial quantity) until the operator makes a
  // deliberate choice here; then the parent may suggest the matching family.
  onAllocationMethodTouched: () => void;
}

const TransportCostsSection: React.FC<TransportCostsSectionProps> = ({
  transport,
  canEditTransportCosts,
  showRateFields,
  multiOrder,
  hasMixedQuantityUnits,
  excludeManual,
  allocationPreview,
  previewColumns,
  currency,
  cargoLineCount,
  warnings,
  renderAllocationWarning,
  ldmFallbackMetric,
  onCostAllocationMethodTouched,
  onAllocationMethodTouched,
}) => {
  const { t } = useTranslation(['transport']);
  const transportRateValue = Form.useWatch('transportRate');

  // Between-orders split key: "Waga" (WEIGHT) is dropped from the picker for new
  // transports (rulebook decision #6). A stored legacy WEIGHT value must still
  // render and stay editable without being silently rewritten, so WEIGHT is
  // offered only when it is already the persisted value on this transport.
  const storedBetweenOrdersMethod = transport?.routeOrders?.[0]?.allocationMethod;
  const betweenOrdersMethods = ['LDM', 'CBM', 'SQM', 'QUANTITY'] as const;
  const betweenOrdersMethodOptions = [
    ...betweenOrdersMethods,
    ...(storedBetweenOrdersMethod === 'WEIGHT' ? (['WEIGHT'] as const) : []),
  ].map((value) => ({
    value,
    label: t(`transport:allocation.methods.${value.toLowerCase()}`),
  }));

  // Item-level split ("Podział kosztu na pozycje") options come from the mirror
  // util's single-home ordering (qty / m² / m³ / LDM / manual) with a
  // per-option tooltip for the dimension-based methods. MANUAL is excluded in
  // split mode via the excludeManual prop.
  const itemAllocationMethodOptions = buildCostAllocationMethodOptionDefs({ excludeManual }).map((definition) => {
    const label = t(`transport:${definition.labelKey}`);
    const tooltip = definition.tooltipKey ? t(`transport:${definition.tooltipKey}`) : null;
    return {
      value: definition.value,
      label: tooltip ? (
        <Space size={4}>
          <span>{label}</span>
          <Tooltip title={tooltip}>
            <InfoCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
          </Tooltip>
        </Space>
      ) : (
        label
      ),
      // Plain-text label kept for the closed-select display and option search.
      title: label,
    };
  });

  return (
    <TransportFormSection
      sectionKey="costs"
      step={4}
      icon={<DollarOutlined />}
      label={canEditTransportCosts ? t('transport:sections.costs') : t('transport:allocation.method')}
      summary={canEditTransportCosts && typeof transportRateValue === 'number' && Number.isFinite(transportRateValue)
        ? formatMoneyPl(transportRateValue, currency || 'PLN')
        : undefined}
    >
      {multiOrder ? (
        <Alert
          type="info"
          showIcon
          message={t('transport:allocation.multiOrderHint')}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {/* Product decision (2026-07-08): Azzura always knows the transport
          price up front and enters it as ONE amount + currency — nobody ever
          enters a per-LDM/m²/m³ rate. The cost section is therefore just the
          amount + currency (below) plus the allocation-method pickers
          further down; the system derives cost/m²/m³/LDM from the amount,
          never the other way round. */}
      <Row gutter={[16, 16]}>
        {canEditTransportCosts && showRateFields && (
          <>
            <Col xs={24} md={12} xl={6}>
              <Form.Item
                name="transportRate"
                label={t('transport:fields.transportRate')}
                rules={[
                  {
                    type: 'number',
                    min: 0,
                    max: MAX_MONEY_AMOUNT,
                    message: t('transport:transportRateOutOfRange', { max: MAX_MONEY_AMOUNT }),
                  },
                ]}
              >
                {/* No min/max clamp: antd silently coerces an out-of-range entry
                    on blur and would save the coerced value. The rule above keeps
                    the entry visible with an inline error and blocks save. */}
                <InputNumber
                  precision={2}
                  style={{ width: '100%' }}
                  {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={6}>
              <Form.Item
                name="currency"
                label={t('transport:fields.currency')}
              >
                <Select options={[{ value: 'USD', label: 'USD' }, { value: 'EUR', label: 'EUR' }, { value: 'PLN', label: 'PLN' }]} />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>

      {hasMixedQuantityUnits ? (
        <Alert
          type="warning"
          showIcon
          message={t('transport:allocation.mixedQuantityUnitsBlocked')}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {/* F5 (group the two allocation selects, small): "Klucz podziału między
          zamówienia" (between orders) and "Podział kosztu na pozycje"
          (item-level) rendered side-by-side under one shared "Podział
          kosztów" heading, instead of the between-orders select living in the
          rate/currency row and the item-level one hiding inside a separate
          Collapse further down. Both stay always-visible (owner: "jedno robimy
          drugie znika") with their existing single-order/single-line hints. */}
      {canEditTransportCosts ? (
        <>
          <Divider orientation="left" style={{ marginTop: 8 }}>
            {t('transport:allocation.groupTitle')}
          </Divider>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12} xl={8}>
              <Form.Item
                name="allocationMethod"
                label={t('transport:allocation.method')}
                tooltip={t('transport:allocation.methodTooltip')}
                extra={!multiOrder ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('transport:betweenOrdersSingleOrderHint')}
                  </Text>
                ) : undefined}
                style={{ marginBottom: 0 }}
              >
                <Select
                  options={betweenOrdersMethodOptions}
                  onChange={onAllocationMethodTouched}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={8}>
              <Form.Item
                name="costAllocationMethod"
                label={t('transport:commercialAllocationMethod')}
                tooltip={t('transport:commercialAllocationMethodTooltip')}
                extra={cargoLineCount === 1 ? (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('transport:costAllocationSingleLineHint')}
                  </Text>
                ) : undefined}
                style={{ marginBottom: 0 }}
              >
                <Select
                  options={itemAllocationMethodOptions}
                  optionLabelProp="title"
                  onChange={onCostAllocationMethodTouched}
                />
              </Form.Item>
            </Col>
          </Row>
        </>
      ) : (
        <>
          {/* Hidden but still registered when the user can't edit costs
              (OPERATIONS): the field values round-trip unchanged. */}
          <Form.Item name="allocationMethod" hidden noStyle>
            <Input />
          </Form.Item>
          <Form.Item name="costAllocationMethod" hidden noStyle>
            <Input />
          </Form.Item>
        </>
      )}

      {/* Per-order allocation preview — only meaningful with ≥2 orders. */}
      {canEditTransportCosts && multiOrder && (
        <>
          <Divider orientation="left" style={{ marginTop: 8 }}>
            {t('transport:allocation.preview')}
          </Divider>
          {/* INDIA (HIGH): the operator picked LDM but at least one order has
              no packaging data, so the WHOLE preview silently fell back to a
              different, uniform metric (or, if none is common, an equal
              split). Non-blocking — the fallback logic itself (never mix
              units across orders) is correct and unchanged; this only makes
              the substitution visible instead of showing a coherent-looking
              table of numbers under an "LDM" label. */}
          {ldmFallbackMetric !== undefined ? (
            <Alert
              type="warning"
              showIcon
              message={ldmFallbackMetric === null
                ? t('transport:allocation.ldmFallbackNoCommonMetric')
                : t('transport:allocation.ldmFallbackWarning', {
                  metric: t(`transport:${ldmBasisMetricLabelKey(ldmFallbackMetric)}`),
                })}
              style={{ marginBottom: 16 }}
            />
          ) : null}
          <Table<SharedTransportPreviewRow>
            rowKey="orderId"
            size="small"
            pagination={false}
            locale={{ emptyText: t('transport:pleaseSelectAtLeastOneOrder') }}
            dataSource={allocationPreview}
            columns={previewColumns}
          />
          <Text type="secondary">
            {t('transport:allocation.previewCurrencyHintSingle', { currency: currency || DEFAULT_TRANSPORT_CURRENCY })}
          </Text>
        </>
      )}

      {/* Item-level allocation warnings stay with the cost section. */}
      {canEditTransportCosts && cargoLineCount > 0
        ? warnings.map(renderAllocationWarning)
        : null}
    </TransportFormSection>
  );
};

export default TransportCostsSection;
